import {useUniversalFetch, useUniversalPost} from '#imports';
import type {WebAttributionProperties} from '../types';
import {useAmplitudeSessionReplayLoader} from './useAmplitudeSessionReplayLoader';
import type {AllTrackingEvents, TrackingEvent} from '../types/data-tracking.events';

const trackingEndpoint = '/api/t/event';
const sessionEndpoint = '/api/t/session';

type TrackingSession = {timestamp: number; timeout: number; amplitudeApiKey: string};

function extractWebAttributionProperties(): WebAttributionProperties {
    const paramsToExtract: Array<keyof WebAttributionProperties> = [
        'utm_source',
        'utm_medium',
        'utm_campaign',
        'utm_term',
        'utm_content',
        'fbclid',
        'gclid',
    ];

    const searchParams = new URLSearchParams(window.location.search);
    const properties: WebAttributionProperties = {};
    for (const param of paramsToExtract) {
        properties[param] = searchParams.get(param) || null;
    }
    return properties;
}

export function useDataTracking() {
    const {sessionReplay} = useAmplitudeSessionReplayLoader();

    async function _trackEvent(
        eventName: string,
        eventProperties: Record<string, unknown> = {},
        userProperties: Record<string, unknown> = {},
    ): Promise<void> {
        // Tracking should only happen client side
        // We want to avoid 403 errors when the POST call is made too early
        // in the page lifecycle and the CSRF token is missing
        if (import.meta.server) {
            return Promise.resolve();
        }

        try {
            return await useUniversalPost<void>(trackingEndpoint, {
                baseURL: '/',
                body: {
                    event_name: eventName,
                    event_properties: eventProperties,
                    user_properties: userProperties,
                    session_replay_properties: sessionReplay.value?.getSessionReplayProperties() ?? {},
                },
            });
        } catch (_: unknown) {
            // Do nothing with error.
            // Alerting / monitoring is done on the api/t/event endpoint (server side)
            return Promise.resolve();
        }
    }

    async function getSessionTimestamp(): Promise<TrackingSession> {
        return await useUniversalFetch<TrackingSession>(sessionEndpoint, {
            baseURL: '/',
        });
    }

    /**
     * To have event properties typecheck, declare your event with {@link TrackingEvent}:
     * - shared events in {@link AllTrackingEvents} (`data-tracking.events.ts`)
     * - app-specific events by augmenting {@link DataTrackingEvents} (`app/configuration/tracking.d.ts`)
     *
     * Note: tracking still works without declarations, but there will be no typecheck
     */
    async function track<
        TEvent extends keyof AllTrackingEvents | (string & {}),
        TProperties extends TrackingEvent = TEvent extends keyof AllTrackingEvents
            ? AllTrackingEvents[TEvent] extends TrackingEvent
                ? AllTrackingEvents[TEvent]
                : TrackingEvent
            : TrackingEvent,
    >(eventName: TEvent, eventProperties: TProperties['properties'] = {}, userProperties: TProperties['userProperties'] = {}): Promise<void> {
        return await _trackEvent(eventName, eventProperties, userProperties);
    }

    async function trackPageView(): Promise<void> {
        if (import.meta.server) {
            return;
        }

        const url = window.location.href.split(window.location.origin)[1];
        const eventProperties = {url};
        const userProperties = {
            ...extractWebAttributionProperties(),
            referrer: document.referrer || null,
            screen_resolution: `${window.screen.width}x${window.screen.height}`,
        };
        return await _trackEvent('page_viewed', eventProperties, userProperties);
    }

    return {
        extractWebAttributionProperties,
        getSessionTimestamp,
        track,
        trackPageView,
    };
}
